import { Box, LinearProgress, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ApartmentCharacteristicsData,
  HouseCharacteristicsData,
  LotCharacteristicsData,
} from '../../api/characteristics/types';
import { PropertyType } from '../../api/enumerations';
import { BoxContainer } from '../../components/UI/Box';
import { BackButton } from '../../components/UI/Button';
import { GridContainer } from '../../components/UI/Grid';
import { IconArrowCircleLeftMS } from '../../constants/icons';
import { parcelGroup, privateUnitsGroup } from '../../constants/selectOptions';
import { ApartmentCharacteristics } from './Apartment';
import { CharacteristicsTitle } from './components/CharacteristicsTitle';
import { HouseCharacteristics } from './House';
import { LotCharacteristics } from './Lot';
import { StyledMobileStepper } from './styles';
import useCharacteristics from './useCharacteristics';

export function CharacteristicsPage(): JSX.Element {
  const {
    characteristicsData,
    propertyType,
    loadingPage,
    completionData,
    propertyData,
  } = useCharacteristics();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const getPageTitle = (): string => {
    if (pathname.includes('/report')) return 'Elaboração de Laudo';
    if (pathname.includes('/archived')) return 'Arquivado';
    return 'Revisão e Aprovação';
  };
  const pageTitle = getPageTitle();
  const isReport = pathname.includes('/report');

  const navigateBack = (): void => {
    navigate(pathname.replace(/\/[^/]+$/, ''));
  };

  if (loadingPage)
    return (
      <Box>
        <LinearProgress sx={{ m: '60px 60px 60px 160px' }} />
      </Box>
    );

  const totalSections = completionData?.total_sections ?? 0;
  const completeSections = completionData?.completed_sections ?? 0;
  const percentage = completionData?.percentage ?? 0;

  const renderCharacteristics = (): JSX.Element => {
    const completionDataForReport = isReport ? completionData : undefined;
    if (parcelGroup.has(propertyType as PropertyType))
      return (
        <LotCharacteristics
          characteristicsData={characteristicsData as LotCharacteristicsData}
          completionData={completionDataForReport}
        />
      );
    if (privateUnitsGroup.has(propertyType as PropertyType))
      return (
        <ApartmentCharacteristics
          characteristicsData={
            characteristicsData as ApartmentCharacteristicsData
          }
          completionData={completionDataForReport}
        />
      );
    return (
      <HouseCharacteristics
        characteristicsData={characteristicsData as HouseCharacteristicsData}
        completionData={completionDataForReport}
      />
    );
  };

  return (
    <GridContainer>
      <BackButton onClick={navigateBack}>{IconArrowCircleLeftMS}</BackButton>
      <BoxContainer>
        <CharacteristicsTitle
          osNumber={propertyData?.reference_number}
          pageTitle={pageTitle}
        />
        <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
          <StyledMobileStepper
            variant="progress"
            steps={101}
            position="static"
            activeStep={percentage}
            nextButton={<Box />}
            backButton={<Box />}
          />
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Box display="flex">
              <Typography variant="caption" color="text.secondary">
                {completeSections} de {totalSections} seções completas
              </Typography>
              <Typography mx={1}>&middot;</Typography>
              <Typography variant="caption" color="text.secondary">
                {completionData?.filled_required_fields ?? 0}/
                {completionData?.total_required_fields ?? 0} campos obrigatórios
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={600} color="primary">
              {percentage}%
            </Typography>
          </Box>
        </Box>
        <Box>
          {characteristicsData && propertyType ? (
            renderCharacteristics()
          ) : (
            <Typography>esta OS não possui características</Typography>
          )}
        </Box>
      </BoxContainer>
    </GridContainer>
  );
}
